/**
 * Plain-language meanings for the CLOSED subscribable event catalogue (OPSFL-FSD-CLIENT-PLATFORM
 * §4 / `WebhookEventCatalogue.java` D7). The SERVER list (`subscribableEventTypes` on
 * GET /v1/subscriptions) is the authority on WHAT is subscribable — this map only explains it.
 * A type the server returns that this console has no words for degrades to an honest
 * "no description yet", never an invented meaning.
 */

export interface EventTypeMeaning {
  /** Functional grouping for scanning the catalogue. */
  group: string;
  /** What an implementer receives when this fires — plain language, payload caveats included. */
  meaning: string;
}

export const EVENT_TYPE_MEANINGS: Record<string, EventTypeMeaning> = {
  'product.workitem.assigned.v1': {
    group: 'Investigator trail',
    meaning: 'A work item (case, alert or task) was assigned to an investigator.',
  },
  'product.workitem.dispositioned.v1': {
    group: 'Investigator trail',
    meaning: 'An investigator decided a work item — the disposition event of the decision trail.',
  },
  'product.rfi.sent.v1': {
    group: 'RFI',
    meaning:
      'A request-for-information was dispatched to the end customer (the human-gated send, ' +
      'never an automatic one).',
  },
  'product.rfi.closed.v1': {
    group: 'RFI',
    meaning: 'A request-for-information was closed — answered, expired or withdrawn.',
  },
  'product.dataroom.invite.created.v1': {
    group: 'Data room',
    meaning: 'A data-room invite was issued to an end customer.',
  },
  'product.dataroom.invite.revoked.v1': {
    group: 'Data room',
    meaning:
      'A data-room invite was revoked — the applicant’s access is cut (live sessions ' +
      'included).',
  },
  'product.dataroom.task.submitted.v1': {
    group: 'Data room',
    meaning:
      'An applicant submitted a data-room task. The payload carries only benign fields ' +
      '(case reference, applicant label, field NAMES) — never document bytes or form answers.',
  },
  'product.tenant.spoke.provisioned.v1': {
    group: 'Tenancy & erasure',
    meaning: 'A new spoke (business unit) was provisioned under this hub.',
  },
  'product.erasure.redacted.v1': {
    group: 'Tenancy & erasure',
    meaning: 'An erasure claim executed — records were redacted over the erasure seam.',
  },
  'product.flow.approval.requested.v1': {
    group: 'Flow governance',
    meaning: 'A flow version was submitted for publish approval (the maker side of the checker trail).',
  },
  'product.flow.approval.approved.v1': {
    group: 'Flow governance',
    meaning: 'A checker approved a flow version for publish.',
  },
  'product.flow.approval.rejected.v1': {
    group: 'Flow governance',
    meaning: 'A checker rejected a flow version — it does not publish.',
  },
  'platform.lifecycle.clock.due.v1': {
    group: 'Lifecycle (pKYC)',
    meaning:
      'A lifecycle clock came due — a periodic review / re-verification is now owed for an entity.',
  },
  'platform.lifecycle.clock.escalated.v1': {
    group: 'Lifecycle (pKYC)',
    meaning: 'A due lifecycle clock went unactioned past its threshold and escalated.',
  },
  'platform.lifecycle.entity.exited.v1': {
    group: 'Lifecycle (pKYC)',
    meaning: 'An entity exited its lifecycle — offboarded or otherwise terminal.',
  },
  // NOT subscribable — appears in the delivery ledger for the Test action only.
  'product.subscription.ping.v1': {
    group: 'Synthetic',
    meaning:
      'The synthetic signed test delivery the "Send test ping" action produces. Not subscribable; ' +
      'a ping never retries (DELIVERED or DEAD in one attempt).',
  },
};

/** Meaning for a type, or null — the caller renders an honest "no description" for null. */
export function describeEventType(type: string): EventTypeMeaning | null {
  return EVENT_TYPE_MEANINGS[type] ?? null;
}
